import type { Locale } from '../context/LocaleContextProvider'

type RawValue = number | null | undefined

const isNumber = (value: RawValue): value is number => typeof value === 'number' && !isNaN(value)

/**
 * @description a credit card fee for the page's locale. English keeps the string the source plugin built
 * (`$120`), so English pages don't change; French formats the raw number (`120 $`).
 * @example formatCardFee({ text: '$120', value: 120 }, 'fr-CA') => '120 $'
 */
export const formatCardFee = ({ text, value }: { text: string; value?: RawValue }, locale: Locale): string => {
  if (locale === 'en-CA' || !isNumber(value)) return text
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'CAD',
    // The default 'symbol' is '$ CA' in Chrome's fr-CA but '$' in Node, so the fee would change after hydration.
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)
}

const isRate = (value: RawValue): value is number => isNumber(value) && value !== 0

// English matches the source plugin's `transformPercentage` (`21.90%`); French uses Intl (`21,90 %`).
const formatPercent = (value: number, locale: Locale): string => {
  if (locale === 'en-CA') return `${(value * 100).toFixed(2)}%`
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * @description a credit card rate for the page's locale. With a higher bound it is a range (`21.90% - 29.90%`),
 * as on the marketplace. Otherwise English keeps the plugin's string (`19.99%`, `N/A`) and French formats the raw
 * fraction (`19,99 %`). A missing or zero rate is empty, so the card shows "not applicable", as the plugin does
 * for zero.
 * @example formatCardRate({ text: '21.90%', value: 0.219, higherBound: 0.299 }, 'en-CA') => '21.90% - 29.90%'
 * @example formatCardRate({ text: '19.99%', value: 0.1999 }, 'fr-CA') => '19,99 %'
 */
export const formatCardRate = (
  { text, value, higherBound }: { text: string; value?: RawValue; higherBound?: RawValue },
  locale: Locale,
): string => {
  if (isRate(value) && isRate(higherBound)) {
    return `${formatPercent(value, locale)} - ${formatPercent(higherBound, locale)}`
  }
  if (locale === 'en-CA') return text
  if (!isRate(value)) return ''
  return formatPercent(value, locale)
}
